import type { KeycloakOnLoad } from "keycloak-js";
import Keycloak from "keycloak-js";
import HTTP from "owp.http";
import { BasePath } from "./BasePath";
import { createHash } from "./createHash";
import type { Role } from "./Roles";
import { Roles } from "./Roles";

const authPath = `${BasePath.rest()}/auth`;
const authService = new HTTP(authPath);
const keycloak = new Keycloak(`${authPath}/keycloak.json`);
let roles = new Set<string>();
let epoch: string | undefined = undefined;
let refreshInterval: NodeJS.Timeout | undefined = undefined;

let onAuthRefreshError: ((cause: unknown) => void) | undefined;

export const Auth = {
    onAuthRefreshError(callback: (cause: unknown) => void): void {
        onAuthRefreshError = callback;
    },

    hasRole(...rolesAllowed: Role[]): boolean {
        return rolesAllowed.some((role) => roles.has(role));
    },

    isReader(): boolean {
        return Auth.hasRole(Roles.READER);
    },

    isStodstrukturenUser(): boolean {
        return Auth.hasRole(Roles.CONTACT_PERSON, Roles.SURVEY_PROVIDER);
    },

    isAdmin(): boolean {
        return Auth.hasRole(Roles.ADMIN);
    },

    isSurveyProvider(): boolean {
        return Auth.hasRole(Roles.SURVEY_PROVIDER);
    },

    isAuthenticated(): boolean {
        return keycloak.authenticated;
    },

    getAuthHeader(): string | undefined {
        return keycloak.token != null ? `Bearer ${keycloak.token}` : undefined;
    },

    // Used to validate caches
    getEpochHeaders(): { "X-Auth-Epoch": string | undefined } {
        return { "X-Auth-Epoch": epoch };
    },

    async getUsername(): Promise<string> {
        const user = await keycloak.loadUserProfile();
        if (user.username == null || user.username === "") {
            throw new Error(`Missing username in user profile`);
        }
        return user.username;
    },

    init(): Promise<boolean> {
        return keycloakInit("check-sso");
    },

    initLoginRequired(): Promise<boolean> {
        return keycloakInit("login-required");
    },

    async login(): Promise<void> {
        await keycloak.login({
            redirectUri: `${window.location.origin}${window.location.pathname}`,
        });
    },

    async logout(): Promise<void> {
        clearInterval(refreshInterval);
        await logoutUserSession();
        await keycloak.logout({
            redirectUri: window.location.origin,
        });
    },
};

async function keycloakInit(onLoad: KeycloakOnLoad): Promise<boolean> {
    const isAuthenticated = await keycloak.init({
        onLoad,
        redirectUri: getInitRedirectUri(),
    });

    if (isAuthenticated) {
        registerRequestInterceptor();
    }

    await initializeUserSession();

    if (isAuthenticated) {
        epoch = getSessionEpoch();
        roles = await getRoles();
    }

    return isAuthenticated;
}

function getInitRedirectUri(): string {
    // Remove login query parameter
    if (window.location.search) {
        return window.location.href.slice(0, -window.location.search.length);
    }

    return window.location.href;
}

function registerRequestInterceptor(): void {
    // oxlint-disable-next-line eslint-plugin-react-hooks/rules-of-hooks
    HTTP.useOptions({
        requestInterceptor: (request) => {
            return {
                ...request,
                headers: {
                    ...request.headers,
                    Authorization: Auth.getAuthHeader(),
                },
            };
        },
    });
}

async function initializeUserSession(): Promise<void> {
    await authService.path("initialize").post();
}

async function logoutUserSession(): Promise<void> {
    await authService.path("logout").post();
}

async function getRoles(): Promise<Set<string>> {
    const r = await authService
        .path("roles")
        .get({ headers: Auth.getEpochHeaders() });
    return new Set(r.json<string[]>());
}

function getSessionEpoch(): string | undefined {
    return keycloak.sessionId != null
        ? createHash(keycloak.sessionId).toString()
        : undefined;
}

// Refresh token if it will expire in the next 60 seconds
function updateToken(): void {
    keycloak.updateToken(60).catch((error: unknown) => {
        if (onAuthRefreshError != null) {
            onAuthRefreshError(error);
        } else {
            console.error("Failed to update Keycloak token", error);
        }
    });
}

// Every 30 seconds, check if the token needs to be refreshed
keycloak.onAuthSuccess = () => {
    clearInterval(refreshInterval);
    refreshInterval = setInterval(updateToken, 30_000);
};
